import {IdentityDocumentType, PartyType} from '@sollapay/enums';
import {findPrimarySignatory, normalizePhoneToLocalDigits} from '@sollapay/utils';
import {useEffect, useRef, useState} from 'react';
import {useForm} from 'react-hook-form';

import {useUpdateBuyerMutation} from '@/features/buyers/hooks';
import {showBuyerUpdatedToast} from '@/features/buyers/view/components';

import type {BuyerDTO, UpdateBuyerPayload} from '@sollapay/types';
import type {FieldNamesMarkedBoolean} from 'react-hook-form';

export interface EditBuyerFormValues {
  partyType: PartyType;
  fullName: string;
  nationalId: string;
  idType: IdentityDocumentType;
  idCountry: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  address: string;
  incorporationNumber: string;
  incorporationDate: string;
  signatory: {
    fullName: string;
    nationalId: string;
    email: string;
    phone: string;
  };
}

export const EMPTY_EDIT_BUYER_FORM_VALUES: EditBuyerFormValues = {
  partyType: PartyType.PERSON,
  fullName: '',
  nationalId: '',
  idType: IdentityDocumentType.NATIONAL_ID,
  idCountry: '',
  email: '',
  phone: '',
  dateOfBirth: '',
  address: '',
  incorporationNumber: '',
  incorporationDate: '',
  signatory: {fullName: '', nationalId: '', email: '', phone: ''}
};

type DirtyFields = Partial<Readonly<FieldNamesMarkedBoolean<EditBuyerFormValues>>>;

export interface UseEditBuyerFormParams {
  trustAccountId: string;
  buyer: BuyerDTO | null;
  onSuccess?: (updatedBuyer: BuyerDTO) => void;
}

export function useEditBuyerForm({trustAccountId, buyer, onSuccess}: UseEditBuyerFormParams) {
  const form = useForm<EditBuyerFormValues>({
    defaultValues: EMPTY_EDIT_BUYER_FORM_VALUES,
    mode: 'onTouched'
  });
  const [isIdentityChangeApproved, setIdentityChangeApproved] = useState(false);

  const updateMutation = useUpdateBuyerMutation(trustAccountId, buyer?.id ?? '');
  const {reset: resetMutation} = updateMutation;
  const isSavingRef = useRef(false);
  isSavingRef.current = updateMutation.isPending;

  // The modal stays mounted across open/close (Modal owns the close animation via `isOpen`),
  // so defaultValues alone won't reflect a newly-opened (or switched) buyer — reset instead.
  // Skipped mid-save: a refetched buyer (e.g. after a document upload) would drop the save's onSuccess.
  useEffect(() => {
    if (!buyer || isSavingRef.current) return;
    form.reset(mapBuyerToEditValues(buyer));
    resetMutation();
  }, [buyer, form, resetMutation]);

  const isIdentityChanged = hasIdentityChanges(form.formState.dirtyFields);

  useEffect(() => {
    if (!isIdentityChanged) setIdentityChangeApproved(false);
  }, [isIdentityChanged]);

  const handleSave = form.handleSubmit(values => {
    const payload = buildUpdatePayload(
      values,
      form.formState.dirtyFields,
      isIdentityChanged ? isIdentityChangeApproved : undefined
    );
    updateMutation.mutate(payload, {
      onSuccess: updatedBuyer => {
        // Re-baseline to the submitted values so isDirty reflects the saved state.
        form.reset(values);
        showBuyerUpdatedToast();
        onSuccess?.(updatedBuyer);
      }
    });
  });

  const resetForm = () => {
    form.reset();
    resetMutation();
  };

  return {
    form,
    handleSave,
    resetForm,
    isSaving: updateMutation.isPending,
    saveError: updateMutation.error,
    isIdentityChanged,
    isIdentityChangeApproved,
    setIdentityChangeApproved
  };
}

function mapBuyerToEditValues(buyer: BuyerDTO): EditBuyerFormValues {
  const primarySignatory = findPrimarySignatory(buyer.signatories);

  return {
    partyType: buyer.partyType,
    fullName: buyer.fullName,
    nationalId: buyer.nationalId ?? '',
    idType: buyer.idType ?? IdentityDocumentType.NATIONAL_ID,
    idCountry: buyer.idCountry ?? '',
    email: buyer.email ?? '',
    phone: normalizePhoneToLocalDigits(buyer.phone ?? ''),
    dateOfBirth: buyer.dateOfBirth ?? '',
    address: buyer.address ?? '',
    incorporationNumber: buyer.organization?.incorporationNumber ?? '',
    incorporationDate: buyer.organization?.incorporationDate ?? '',
    signatory: {
      fullName: primarySignatory?.fullName ?? '',
      nationalId: primarySignatory?.nationalId ?? '',
      email: primarySignatory?.email ?? '',
      phone: normalizePhoneToLocalDigits(primarySignatory?.phone ?? '')
    }
  };
}

function hasIdentityChanges(dirty: DirtyFields): boolean {
  return Boolean(
    dirty.fullName ||
    dirty.nationalId ||
    dirty.idCountry ||
    dirty.dateOfBirth ||
    dirty.address ||
    dirty.incorporationNumber ||
    dirty.incorporationDate ||
    dirty.signatory?.fullName ||
    dirty.signatory?.nationalId
  );
}

function dirtyValue<T>(isDirty: boolean | undefined, value: T): T | undefined {
  return isDirty ? value : undefined;
}

function buildUpdatePayload(
  values: EditBuyerFormValues,
  dirty: DirtyFields,
  approveIdentityChange: boolean | undefined
): UpdateBuyerPayload {
  if (values.partyType === PartyType.ORGANIZATION) {
    return {
      partyType: PartyType.ORGANIZATION,
      companyName: dirtyValue(dirty.fullName, values.fullName),
      incorporationNumber: dirtyValue(dirty.incorporationNumber, values.incorporationNumber),
      incorporationDate: dirtyValue(dirty.incorporationDate, values.incorporationDate),
      address: values.address,
      signatory: {
        fullName: dirtyValue(dirty.signatory?.fullName, values.signatory.fullName),
        nationalId: dirtyValue(dirty.signatory?.nationalId, values.signatory.nationalId),
        phone: values.signatory.phone,
        email: values.signatory.email
      },
      approveIdentityChange
    };
  }

  return {
    partyType: PartyType.PERSON,
    fullName: dirtyValue(dirty.fullName, values.fullName),
    nationalId: dirtyValue(dirty.nationalId, values.nationalId),
    idCountry: dirtyValue(dirty.idCountry, values.idCountry),
    dateOfBirth: dirtyValue(dirty.dateOfBirth, values.dateOfBirth),
    phone: values.phone,
    email: values.email,
    address: values.address,
    approveIdentityChange
  };
}
